import { lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, redirect, RouteObject } from 'react-router-dom';
import { createClientViewAuthorization, fetchAuth } from '~/apis/auth';
import App from '~/App';
import AxiosInterceptorWrapper from '~/components/Dashboard/AxiosInterceptorWrapper';
import { AppLayout } from '~/components/layout/AppLayout';
import { AuthLayout } from '~/components/layout/AuthLayout';
import { ClientViewLoginErrorView } from '~/components/layout/ClientView/ClientViewLoginError';
import LoadingCircularProgress from '~/components/LoadingCircularProgress';
import { ErrorBoundaryView } from '~/pages/ErrorPage/ErrorBoundary';
import { CONVERSATIONAL_ASSISTANT_ROUTES } from '~/router/pages/conversationalAssistant';
import { DOMAIN_NAMES_ROUTES } from '~/router/pages/domainNames';
import { GENERATIVE_AI_ROUTES } from '~/router/pages/generativeAI';
import { MARKETPLACE_ROUTES } from '~/router/pages/marketplace';
import { OKTA_ROUTES } from '~/router/pages/okta';
import { PERFORMANCE_ROUTES } from '~/router/pages/performance';
import { PORTFOLIO_ROUTES } from '~/router/pages/portfolio';
import { PRICE_MONITORING_ROUTES } from '~/router/pages/priceMonitoring';
import { RESET_PASSWORD_ROUTES } from '~/router/pages/resetPassword';
import { RIOT_OKTA_ROUTES } from '~/router/pages/riotOkta';
import { SELLER_ANALYTICS_ROUTES } from '~/router/pages/sellerAnalytics';
import { SOCIAL_MEDIA_ROUTES } from '~/router/pages/socialMedia';
import { WEBSITE_CONTENT_ROUTES } from '~/router/pages/websiteContent';
import { RootRouteEnum, SettingsRouteEnum } from './@types';
import { PermissionMap } from './permissionMap';

// pages
const LoginPage = lazy(() => import('../components/User/LoginPage'));
const VerifyEmailPage = lazy(() => import('../components/User/VerifyEmailPage'));
const CallbackVerifyEmailPage = lazy(() => import('../components/User/CallbackPage/VerifyEmail'));
const SignUpPage = lazy(() => import('../components/User/SignUpPage'));
const SignUpComplete = lazy(() => import('../components/User/SignUpComplete'));
const HomePage = lazy(() => import('~/pages/Home'));
const SettingsPage = lazy(() => import('~/pages/Settings'));
const SellerIntelligenceLabPage = lazy(() => import('~/pages/SellerIntelligence/Lab'));
const SellerLink = lazy(() => import('~/pages/SellerLink'));
const NotFoundPage = lazy(() => import('~/pages/ErrorPage/NotFound'));

const ROUTES: RouteObject[] = [
  {
    path: '',
    loader: async () => {
      const authData = await fetchAuth();
      if (authData) {
        return redirect(`/${RootRouteEnum.Home}`);
      } else {
        return redirect(`/${RootRouteEnum.Login}`);
      }
    },
    errorElement: <Sentry.ErrorBoundary fallback={(errorData) => <ErrorBoundaryView errorData={errorData} />} />,
  },
  {
    path: '/',
    element: <App />,
    errorElement: <Sentry.ErrorBoundary fallback={(errorData) => <ErrorBoundaryView errorData={errorData} />} />,
    children: [
      //#region >>>>> Sign up / oAuth Login
      {
        path: RootRouteEnum.Auth,
        children: RIOT_OKTA_ROUTES,
      },
      {
        path: RootRouteEnum.Okta,
        children: OKTA_ROUTES,
      },
      {
        path: `${RootRouteEnum.Dashboard}/:access_token/:refresh_token/:opscc_user/:client_id`,
        loader: async ({ params, request }) => {
          const { searchParams } = new URL(request.url);

          if (params.access_token && params.refresh_token && params.opscc_user) {
            const res = await createClientViewAuthorization(params.access_token, params.refresh_token, {
              client_id: Number(params.client_id),
            });

            if (res === 'success') {
              const rawRedirectTo = searchParams.get('redirectTo');
              if (!rawRedirectTo) return redirect('/home');

              const redirectTo = rawRedirectTo.startsWith('/') ? rawRedirectTo : `/${rawRedirectTo}`;
              return redirect(redirectTo);
            } else {
              throw new Error('Failed to login');
            }
          }
          return null;
        },
        errorElement: <ClientViewLoginErrorView />,
      },
      {
        path: '',
        element: <AuthLayout />,
        children: [
          {
            path: RootRouteEnum.Login,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <LoginPage />
              </Suspense>
            ),
          },
          {
            path: `${RootRouteEnum.VerifyEmail}/:token`,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <VerifyEmailPage />
              </Suspense>
            ),
          },
          {
            path: `${RootRouteEnum.Signup}/complete`,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <SignUpComplete />
              </Suspense>
            ),
          },
          {
            path: `${RootRouteEnum.Signup}/:token`,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <SignUpPage />
              </Suspense>
            ),
          },
          // note-@blue-poolyum: 2FA verification page
          {
            path: `${RootRouteEnum.Callback}/${RootRouteEnum.VerifyEmail}/:uuid`,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <CallbackVerifyEmailPage />
              </Suspense>
            ),
          },
          {
            path: RootRouteEnum.ResetPassword,
            children: RESET_PASSWORD_ROUTES,
          },
        ],
      },
      //#endregion <<<<<< Sign up / oAuth Login
      {
        path: '',
        element: <AppLayout />,
        loader: async () => {
          const authData = await fetchAuth();
          if (!authData) {
            return redirect(`/${RootRouteEnum.Login}`);
          }
          return null;
        },
        children: [
          {
            path: RootRouteEnum.Home,
            index: true,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <HomePage />
              </Suspense>
            ),
          },
          {
            path: `${RootRouteEnum.Settings}/:settingsTab?/:subTab?`,
            loader: async ({ params }) => {
              if (!Object.values(SettingsRouteEnum).includes((params.settingsTab as SettingsRouteEnum) || '')) {
                return redirect(`/${RootRouteEnum.Settings}/${SettingsRouteEnum.Account}`);
              } else {
                const hasAccess = await PermissionMap.SettingTab();
                if (
                  (params.settingsTab === SettingsRouteEnum.Billing && !hasAccess.billing) ||
                  (params.settingsTab === SettingsRouteEnum.People && !hasAccess.people)
                ) {
                  return redirect(`/${RootRouteEnum.Settings}/${SettingsRouteEnum.Account}`);
                } else {
                  return new Response(JSON.stringify(hasAccess), {
                    status: 200,
                  });
                }
              }
            },
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <SettingsPage />
              </Suspense>
            ),
          },
          {
            path: RootRouteEnum.Analytics,
            children: PERFORMANCE_ROUTES,
          },
          {
            path: RootRouteEnum.Portfolio,
            children: PORTFOLIO_ROUTES,
          },
          {
            path: RootRouteEnum.Marketplace,
            loader: async () => {
              const hasAccess = await PermissionMap.Marketplace();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect(`/${RootRouteEnum.Home}`);
              }
            },
            children: MARKETPLACE_ROUTES,
          },
          {
            path: RootRouteEnum.SocialMedia,
            loader: async () => {
              const hasAccess = await PermissionMap.SocialMedia();
              const hasPiracyAccess = await PermissionMap.PiracySocialMedia();
              const hasAllSocialMediaAccess = await PermissionMap.AllSocialMedia();

              if (hasAccess || hasPiracyAccess || hasAllSocialMediaAccess) {
                return null; // bypass
              } else {
                return redirect(`/${RootRouteEnum.Home}`);
              }
            },

            children: SOCIAL_MEDIA_ROUTES,
          },
          {
            path: RootRouteEnum.WebsiteContent,
            loader: async () => {
              const hasAccess = await PermissionMap.WebsiteContent();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect(`/${RootRouteEnum.Home}`);
              }
            },
            children: WEBSITE_CONTENT_ROUTES,
          },
          {
            path: RootRouteEnum.DomainNames,
            loader: async () => {
              const hasAccess = await PermissionMap.DomainNames();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect(`/${RootRouteEnum.Home}`);
              }
            },
            children: DOMAIN_NAMES_ROUTES,
          },
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <AxiosInterceptorWrapper />
              </Suspense>
            ),
            children: [
              {
                path: RootRouteEnum.SellerAnalytics,
                loader: async () => {
                  const hasAccess = await PermissionMap.Seller();
                  if (hasAccess) {
                    return null; // bypass
                  } else {
                    return redirect(`/${RootRouteEnum.Home}`);
                  }
                },
                children: SELLER_ANALYTICS_ROUTES,
              },
              {
                path: `${RootRouteEnum.SellerLink}/:historyId?/:historyDetailId?`,
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <SellerLink />
                  </Suspense>
                ),
              },

              {
                path: `${RootRouteEnum.SellerIntel}/:clusterId?`,
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <SellerIntelligenceLabPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: RootRouteEnum.PriceMonitoring,
            loader: async () => {
              const hasAccess = await PermissionMap.PriceMonitoring();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect(`/${RootRouteEnum.Home}`);
              }
            },
            children: PRICE_MONITORING_ROUTES,
          },
          {
            path: RootRouteEnum.ConversationalAssistant,
            loader: async () => {
              const hasAccess = await PermissionMap.ConversationalAssistant();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect(`/${RootRouteEnum.Home}`);
              }
            },
            children: CONVERSATIONAL_ASSISTANT_ROUTES,
          },
          {
            path: RootRouteEnum.GenerativeAI,
            loader: async () => {
              const hasAccess = await PermissionMap.GenerativeAI();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect(`/${RootRouteEnum.Home}`);
              }
            },
            children: GENERATIVE_AI_ROUTES,
          },
        ],
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
];

export const MARQ_AI_CLIENT_ROUTER = createBrowserRouter(ROUTES);
